/**
 * MADE-065: ManufacturingHub
 * Converged /manufacturing view (nav label: "Production").
 * Replaces ManufacturingKanban + ProductionManagement (staff order view).
 *
 * API compatibility layer:
 * - Reads ManufacturingOrders from /api/madebb/manufacturing-orders ✅
 * - Reads roll shade cards from /api/madebb/production-queue (type='order') ✅
 * - AssemblyOrders and ProductionOrders adapters are wired but endpoints don't exist yet (MADE-066/067)
 *
 * DO NOT touch /status/:orderId — that's customer-facing and unchanged.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Alert, CircularProgress,
  Tooltip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Snackbar,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { authFetch } from '../../utils/authFetch';
import { HighlightsBar } from './HighlightsBar';
import { KanbanBoard } from './KanbanBoard';
import { OrderDetailDrawer } from './OrderDetailDrawer';
import { InventoryPriorityPanel } from './InventoryPriorityPanel';
import { CustomerOrderPackSlip } from './CustomerOrderPackSlip';
import {
  adaptManufacturingOrder,
  adaptAssemblyOrder,
} from './workOrderAdapter';
import type {
  WorkOrder, ManufacturingOrder, StockAlert, Material, ProductionQueueItem, AssemblyOrder, PackSlipData,
} from '../../types/madebb.types';

// ── ManufacturingHub ─────────────────────────────────────────────────────────

export const ManufacturingHub: React.FC = () => {

  // ── State ──────────────────────────────────────────────────────────────

  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [activeOrderCount, setActiveOrderCount] = useState(0);
  const [stockAlerts, setStockAlerts] = useState<StockAlert[]>([]);
  const [machines, setMachines] = useState<{ id: string; name: string }[]>([]);
  const [produceList, setProduceList] = useState<Material[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Drawer
  const [drawerOrder, setDrawerOrder] = useState<WorkOrder | null>(null);

  // Assign dialog
  const [assignDialog, setAssignDialog] = useState<{ open: boolean; wo: WorkOrder | null }>({ open: false, wo: null });
  const [selectedMachine, setSelectedMachine] = useState('');
  const [loadingActions, setLoadingActions] = useState<Record<string, boolean>>({});

  // QC fail dialog
  const [qcFailDialog, setQcFailDialog] = useState<{ open: boolean; wo: WorkOrder | null }>({ open: false, wo: null });
  const [rejectionReason, setRejectionReason] = useState('');

  // Pack slip
  const [packSlipDialog, setPackSlipDialog] = useState<{ open: boolean; data: PackSlipData | null; orderNumber: string }>({ open: false, data: null, orderNumber: '' });


  // Send to production
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());

  // ── Data loading ───────────────────────────────────────────────────────

  const loadManufacturingOrders = useCallback(async () => {
    const res = await authFetch('/api/madebb/manufacturing-orders?sort=due_date');
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to load manufacturing orders');
    return (data.orders as ManufacturingOrder[]).map(adaptManufacturingOrder);
  }, []);

  const loadProductionQueue = useCallback(async () => {
    // MADE-092f: This function is now deprecated. All order types, including rollshades,
    // are sourced from manufacturing-orders. This function is left temporarily as a no-op
    // to prevent breaking changes in other parts of the app that might still call it.
    // It will be removed in a future sprint.
    return [];
  }, []);

  const loadAssemblyOrders = useCallback(async () => {
    const res = await authFetch('/api/madebb/assembly-orders');
    const data = await res.json();
    if (!data.success) return [];
    return (data.orders as AssemblyOrder[]).map(adaptAssemblyOrder);
  }, []);
  //
  // const loadProductionOrders = useCallback(async () => {
  //   const res = await authFetch('/api/madebb/production-orders');
  //   const data = await res.json();
  //   if (!data.success) return [];
  //   return (data.orders as ProductionOrder[]).map(adaptProductionOrder);
  // }, []);

  const loadStockAlerts = useCallback(async () => {
    try {
      const res = await authFetch('/api/madebb/stock-alerts?resolved=false');
      const data = await res.json();
      if (data.success) setStockAlerts(data.alerts || []);
    } catch {
      // Non-blocking
    }
  }, []);

  const loadMachines = useCallback(async () => {
    try {
      const res = await authFetch('/api/madebb/machines');
      const data = await res.json();
      if (data.machines) setMachines(data.machines || []);
    } catch {
      // Non-blocking
    }
  }, []);

  const loadProduceList = useCallback(async () => {
    try {
      const res = await authFetch('/api/madebb/inventory/produce-list');
      const data = await res.json();
      if (data.success && data.produceList) {
        setProduceList(data.produceList.items || []);
      }
    } catch {
      // Non-blocking
    }
  }, []);

  const loadActiveOrderCount = useCallback(async () => {
    try {
      const res = await authFetch('/api/madebb/manufacturing-orders/active-count');
      const data = await res.json();
      if (data.success) setActiveOrderCount(data.count);
    } catch {
      // Non-blocking, defaults to 0
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mfgOrders, queueOrders, assemblyOrders] = await Promise.all([
        loadManufacturingOrders(),
        loadProductionQueue(),
        loadAssemblyOrders(),
      ]);
      // Merge all order streams; future: add productionOrders
      setWorkOrders([...mfgOrders, ...queueOrders, ...assemblyOrders]);
      await Promise.all([loadStockAlerts(), loadMachines(), loadProduceList(), loadActiveOrderCount()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [loadManufacturingOrders, loadProductionQueue, loadStockAlerts, loadMachines, loadProduceList, loadActiveOrderCount]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refresh = () => loadAll();

  // ── Actions ────────────────────────────────────────────────────────────

  const handleAssignSubmit = async () => {
    if (!assignDialog.wo || !selectedMachine) return;
    const wo = assignDialog.wo;
    const rawOrder = wo._raw as ManufacturingOrder;
    const actionKey = `assign-${wo.id}`;
    setLoadingActions(p => ({ ...p, [actionKey]: true }));
    try {
      const assignRes = await authFetch(`/api/madebb/manufacturing-orders/${rawOrder.id}/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ machine: selectedMachine }),
      });
      const assignData = await assignRes.json();
      if (!assignData.success) throw new Error(assignData.error);

      const statusRes = await authFetch(`/api/madebb/manufacturing-orders/${rawOrder.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'in_production' }),
      });
      const statusData = await statusRes.json();
      if (!statusData.success) throw new Error(statusData.error);

      setSuccessMessage(`Order ${rawOrder.order_number} assigned to ${selectedMachine} and moved to In Production`);
      setAssignDialog({ open: false, wo: null });
      setSelectedMachine('');
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign order');
    } finally {
      setLoadingActions(p => ({ ...p, [actionKey]: false }));
    }
  };

  const handleQcPass = async (wo: WorkOrder) => {
    const rawOrder = wo._raw as ManufacturingOrder;
    const actionKey = `qc-pass-${wo.id}`;
    setLoadingActions(p => ({ ...p, [actionKey]: true }));
    try {
      const res = await authFetch(`/api/madebb/manufacturing-orders/${rawOrder.id}/qc-pass`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSuccessMessage(`QC passed for ${rawOrder.order_number}. Pack slip generated.`);
      if (data.packSlip) {
        setPackSlipDialog({ open: true, data: data.packSlip, orderNumber: rawOrder.order_number });
      }
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record QC pass');
    } finally {
      setLoadingActions(p => ({ ...p, [actionKey]: false }));
    }
  };

  const handleQcFailSubmit = async () => {
    if (!qcFailDialog.wo || !rejectionReason.trim()) return;
    const wo = qcFailDialog.wo;
    const rawOrder = wo._raw as ManufacturingOrder;
    const actionKey = `qc-fail-${wo.id}`;
    setLoadingActions(p => ({ ...p, [actionKey]: true }));
    try {
      const res = await authFetch(`/api/madebb/manufacturing-orders/${rawOrder.id}/qc-fail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rejection_reason: rejectionReason }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSuccessMessage(`QC failure recorded for ${rawOrder.order_number}. Returned to In Production.`);
      setQcFailDialog({ open: false, wo: null });
      setRejectionReason('');
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record QC failure');
    } finally {
      setLoadingActions(p => ({ ...p, [actionKey]: false }));
    }
  };

  const handleViewPackSlip = async (wo: WorkOrder) => {
    const rawOrder = wo._raw as ManufacturingOrder;
    try {
      const res = await authFetch(`/api/madebb/manufacturing-orders/${rawOrder.id}/pack-slip`);
      const data = await res.json();
      if (data.success) setPackSlipDialog({ open: true, data: data.packSlip, orderNumber: rawOrder.order_number });
      else setError(data.error);
    } catch {
      setError('Failed to load pack slip');
    }
  };

  const handleKanbanStatusChange = async (wo: WorkOrder, newStatus: WorkOrder['kanbanStatus']) => {
    try {
      let url: string;
      let body: Record<string, string>;
      let method = 'PUT';

      switch (wo.type) {
        // MADE-092f: Rollshade orders now use the same status endpoint as manufacturing orders.
        // The logic is identical, so they can be treated the same way.
        case 'roll_shade':
        case 'assembly':
          url = `/api/madebb/assembly-orders/${wo.id}/status`;
          body = { status: newStatus };
          break;
        default:
          url = `/api/madebb/manufacturing-orders/${wo.id}/status`;
          body = { status: newStatus };
      }

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update order status');
    }
  };

  const handleMarkShipped = async (wo: WorkOrder) => {
    const actionKey = `ship-${wo.id}`;
    setLoadingActions(p => ({ ...p, [actionKey]: true }));
    try {
      // MADE-092f: Rollshade orders now use the same status endpoint as manufacturing orders.
      const url = wo.type === 'assembly'
        ? `/api/madebb/assembly-orders/${wo.id}/ship`
        : `/api/madebb/manufacturing-orders/${wo.id}/ship`;


      const res = await authFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSuccessMessage(`Order ${wo.orderNumber} marked as shipped.`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark order as shipped');
    } finally {
      setLoadingActions(p => ({ ...p, [actionKey]: false }));
    }
  };

  const handleResolveAlert = async (alertId: number) => {
    try {
      await authFetch(`/api/madebb/stock-alerts/${alertId}/resolve`, { method: 'PUT' });
      await loadStockAlerts();
    } catch {
      // Non-blocking
    }
  };

  const handleSendToProduction = async (materialId: string, quantity: number, materialName: string, bom?: { materialId: string; quantity: number; notes?: string }[]) => {
    const actionKey = `send-prod-${materialId}`;
    // BUG-21 fix: update sendingIds so the button is disabled while request is in-flight,
    // preventing double-click from creating duplicate orders.
    setSendingIds(prev => new Set(prev).add(materialId));
    setLoadingActions(p => ({ ...p, [actionKey]: true }));
    try {
      const res = await authFetch('/api/madebb/manufacturing-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: `Produce ${materialName}`,
          material_name: materialName,
          // MADE-094 regression fix: guard against reorderQuantity=0/NaN (reorder_quantity
          // NULL or zero in DB). Fall back to 1 so validation passes; the server's
          // triggerFulfilmentForOrder will use the material's own reorder_quantity anyway.
          quantity: (Number.isFinite(quantity) && quantity > 0) ? quantity : 1,
          unit: 'pieces',
          bom: bom || [],
          // BUG-18 fix: send order_type='stock_replenishment' so triggerFulfilmentForOrder
          // is NOT called on the server. Without this, the server defaults to 'standard'
          // and auto-routes to pack_and_ship when current_stock >= reorder_quantity.
          // Stock replenishment orders must always start at 'received' (in production).
          order_type: 'stock_replenishment',
          // Send material_id so the server can record the link.
          material_id: materialId,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to send to production');
      setSuccessMessage(`${materialName} sent to production — order ${data.order?.order_number || ''} created.`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send to production');
    } finally {
      setSendingIds(prev => { const s = new Set(prev); s.delete(materialId); return s; });
      setLoadingActions(p => ({ ...p, [actionKey]: false }));
    }
  };

  // Generic advance handler for all work order types
  const handleAdvance = async (wo: WorkOrder, newStatus: WorkOrder['kanbanStatus']) => {
    const actionKey = `advance-${wo.id}`;
    setLoadingActions(p => ({ ...p, [actionKey]: true }));
    try {
      // This logic is consolidated from the old handleKanbanStatusChange and roll shade handlers
      let url: string;
      let body: Record<string, string>;
      let method = 'PUT';

      switch (wo.type) {
        // MADE-092f: Rollshade orders now use the manufacturing_orders endpoint.
        // Their status updates are handled like any other manufacturing order.
        case 'roll_shade':
        case 'manufacturing':
        default:
          url = `/api/madebb/manufacturing-orders/${wo.id}/status`;
          body = { status: newStatus };
          break;
        case 'assembly':
          // Future: Assembly order status update endpoint
          url = `/api/madebb/assembly-orders/${wo.id}/status`;
          body = { status: newStatus };
          break;

      }

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSuccessMessage(`Order ${wo.orderNumber} advanced to ${newStatus}.`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to advance order status');
    } finally {
      setLoadingActions(p => ({ ...p, [actionKey]: false }));
    }
  };

  // BUG-37: Rollshades ship direct to customer — set status to 'completed' via production-queue status endpoint.
  // No pack slip, no inventory logic. Uses production_id (not manufacturing order id).
  // NOT handleKanbanStatusChange with 'pack_and_ship' (which re-sends wrong status).
  // Skip's decision: no inventory increment or decrement for rollshade ship.
  // DEPRECATED by MADE-092f: This function is no longer needed as rollshade orders
  // are shipped via the same flow as manufacturing orders.
  const handleMarkShippedRollShade = async (wo: WorkOrder) => {
    // This is now a no-op. The generic `handleMarkShipped` handles all types.
    console.warn('handleMarkShippedRollShade is deprecated and should be removed.');
    await handleMarkShipped(wo);
  };

  // Cancel Order — for received/blocked manufacturing orders
  const handleCancelOrder = async (wo: WorkOrder) => {
    const actionKey = `cancel-${wo.id}`;
    setLoadingActions(p => ({ ...p, [actionKey]: true }));
    try {
      const res = await authFetch(`/api/madebb/manufacturing-orders/${wo.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'cancelled' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? 'Failed to cancel order');
      }
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel order');
    } finally {
      setLoadingActions(p => ({ ...p, [actionKey]: false }));
    }
  };

  // Assembly Order Actions
  const handleAssignMachineAssembly = (orderId: string) => {
    console.log('Assign machine for assembly order:', orderId);
    // TODO: Implement machine assignment modal for assembly orders
  };

  const handleAdvanceStatusAssembly = (orderId: string) => {
    console.log('Advance status for assembly order:', orderId);
    // TODO: Implement status advancement for assembly orders
  };

  const handleQCPassAssembly = (orderId: string) => {
    console.log('QC Pass for assembly order:', orderId);
    // TODO: Implement QC Pass for assembly orders
  };

  const handleQCFailAssembly = (orderId: string) => {
    console.log('QC Fail for assembly order:', orderId);
    // TODO: Implement QC Fail for assembly orders
  };

  // ── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page header (now in MadebbLayout) */}

      {/* Feedback */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Section A: Highlights Bar */}
      <HighlightsBar
        workOrders={workOrders}
        activeOrderCount={activeOrderCount}
        stockAlertCount={stockAlerts.length}
      />

      {/* Section B: Inventory Priority Panel */}
      <InventoryPriorityPanel
        produceList={produceList}
        stockAlerts={stockAlerts}
        onSendToProduction={handleSendToProduction}
        sendingIds={sendingIds}
        onResolveAlert={handleResolveAlert}
      />

      {/* Section A: Kanban Board */}
      <KanbanBoard
        workOrders={workOrders}
        onAssign={wo => { setAssignDialog({ open: true, wo }); setSelectedMachine(''); }}
        onQcPass={handleQcPass}
        onQcFail={wo => { setQcFailDialog({ open: true, wo }); setRejectionReason(''); }}
        onViewPackSlip={handleViewPackSlip}
        onViewDetail={setDrawerOrder}
        onStatusChange={handleKanbanStatusChange} // Keep for drag-drop
        onAdvance={handleAdvance} // New generic advance for buttons
        onMarkShipped={handleMarkShipped}
        onMarkShippedRollShade={handleMarkShippedRollShade}
        onCancel={handleCancelOrder}
        loadingActions={loadingActions}
        onAssignMachineAssembly={handleAssignMachineAssembly}
        onAdvanceStatusAssembly={handleAdvanceStatusAssembly}
        onQCPassAssembly={handleQCPassAssembly}
        onQCFailAssembly={handleQCFailAssembly}
      />

      {/* Order Detail Drawer */}
      <OrderDetailDrawer workOrder={drawerOrder} onClose={() => setDrawerOrder(null)} />

      {/* Assign Machine Dialog */}
      <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ open: false, wo: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Machine — {(assignDialog.wo?._raw as ManufacturingOrder)?.order_number}</DialogTitle>
        <DialogContent>
          {assignDialog.wo && (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {(assignDialog.wo._raw as ManufacturingOrder).material_name}
                {' · '}
                {(assignDialog.wo._raw as ManufacturingOrder).quantity}{' '}
                {(assignDialog.wo._raw as ManufacturingOrder).unit}
                {(assignDialog.wo._raw as ManufacturingOrder).dimensions ? ` · ${(assignDialog.wo._raw as ManufacturingOrder).dimensions}` : ''}
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Select machine / printer</InputLabel>
                <Select
                  value={selectedMachine}
                  label="Select machine / printer"
                  onChange={e => setSelectedMachine(e.target.value)}
                >
                  {machines.map(m => (
                    <MenuItem key={m.id} value={m.name}>{m.name}</MenuItem>
                  ))}
                  {machines.length === 0 && (
                    <MenuItem disabled>No machines configured</MenuItem>
                  )}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, wo: null })} disabled={loadingActions[`assign-${assignDialog.wo?.id}`]}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignSubmit}
            disabled={!selectedMachine || loadingActions[`assign-${assignDialog.wo?.id}`]}
            startIcon={loadingActions[`assign-${assignDialog.wo?.id}`] ? <CircularProgress size={16} /> : <BuildIcon />}
          >
            Assign & Start Production
          </Button>
        </DialogActions>
      </Dialog>

      {/* QC Fail Dialog */}
      <Dialog open={qcFailDialog.open} onClose={() => setQcFailDialog({ open: false, wo: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Record QC Failure — {(qcFailDialog.wo?._raw as ManufacturingOrder)?.order_number}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This will return the order to <strong>In Production</strong> and record the rejection reason.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection reason (required)"
            value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            placeholder="e.g. Print color saturation below tolerance"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQcFailDialog({ open: false, wo: null })} disabled={loadingActions[`qc-fail-${qcFailDialog.wo?.id}`]}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleQcFailSubmit}
            disabled={!rejectionReason.trim() || loadingActions[`qc-fail-${qcFailDialog.wo?.id}`]}
            startIcon={loadingActions[`qc-fail-${qcFailDialog.wo?.id}`] ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            Record Failure
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pack Slip Dialog */}
      {packSlipDialog.open && packSlipDialog.data && (
        <CustomerOrderPackSlip
          packSlipData={packSlipDialog.data}
          orderNumber={packSlipDialog.orderNumber}
          onClose={() => setPackSlipDialog({ open: false, data: null, orderNumber: '' })}
        />
      )}
    </Box>
  );
};
